import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  householdId: string
  onComplete:  () => void
  onSkip:      () => void
}

interface Room {
  id:         string
  label:      string
  emoji:      string
  color:      string
  colorLight: string
  hint:       string
  examples:   string[]
}

const ROOMS: Room[] = [
  {
    id:         'fridge',
    label:      'Fridge',
    emoji:      '🧊',
    color:      '#4A90D9',
    colorLight: '#E8F2FF',
    hint:       'Open your fridge and scan or add what you see',
    examples:   ['Milk', 'Eggs', 'Butter', 'Cheese', 'Yogurt', 'Leftovers', 'Juice'],
  },
  {
    id:         'freezer',
    label:      'Freezer',
    emoji:      '❄️',
    color:      '#7B68EE',
    colorLight: '#F0EEFF',
    hint:       'Check your freezer for meats, frozen meals, and ice cream',
    examples:   ['Chicken', 'Ground beef', 'Frozen vegetables', 'Ice cream', 'Frozen pizza'],
  },
  {
    id:         'pantry',
    label:      'Pantry',
    emoji:      '🏠',
    color:      '#6B9E78',
    colorLight: '#E8F2EA',
    hint:       'Walk through your pantry shelves',
    examples:   ['Pasta', 'Rice', 'Canned tomatoes', 'Olive oil', 'Flour', 'Sugar', 'Coffee'],
  },
  {
    id:         'cabinets',
    label:      'Cabinets',
    emoji:      '🗄️',
    color:      '#D4863A',
    colorLight: '#FDF0E3',
    hint:       'Check kitchen cabinets and countertops',
    examples:   ['Cereal', 'Bread', 'Snacks', 'Spices', 'Tea', 'Protein bars'],
  },
  {
    id:         'countertop',
    label:      'Countertop',
    emoji:      '🍳',
    color:      '#C25B4E',
    colorLight: '#FAEAE8',
    hint:       'What\'s sitting out on your counter?',
    examples:   ['Bananas', 'Apples', 'Onions', 'Garlic', 'Potatoes', 'Bread'],
  },
]

// ─── PantrySetupFlow ──────────────────────────────────────────────────────────

export default function PantrySetupFlow({ householdId, onComplete, onSkip }: Props) {
  const queryClient = useQueryClient()

  const [step,       setStep]       = useState<'intro' | 'rooms' | 'done'>('intro')
  const [roomIndex,  setRoomIndex]  = useState(0)
  const [itemInput,  setItemInput]  = useState('')
  const [addedItems, setAddedItems] = useState<{ name: string; location: string }[]>([])
  const [saving,     setSaving]     = useState(false)
  const [roomCounts, setRoomCounts] = useState<Record<string, number>>({})

  const currentRoom = ROOMS[roomIndex]
  const totalAdded  = addedItems.length

  // ── Add item ─────────────────────────────────────────────────────────────────

  async function handleAddItem(name: string) {
    if (!name.trim()) return
    const trimmed = name.trim()

    setSaving(true)
    try {
      await supabase.from('pantry_items').insert({
        household_id:       householdId,
        name:               trimmed.charAt(0).toUpperCase() + trimmed.slice(1),
        emoji:              getEmoji(trimmed),
        display_category:   getCategory(currentRoom.id),
        freshness_category: getFreshness(currentRoom.id),
        is_active:          true,
        confirmation_count: 3,
        last_confirmed_at:  new Date().toISOString(),
        location:           currentRoom.id,
        track_expiration:   ['fridge', 'freezer'].includes(currentRoom.id),
        status_override:    null,
      })

      setAddedItems(prev => [...prev, { name: trimmed, location: currentRoom.id }])
      setRoomCounts(prev => ({ ...prev, [currentRoom.id]: (prev[currentRoom.id] ?? 0) + 1 }))
      setItemInput('')
      queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
    } catch (err) {
      console.error('Add item error:', err)
    } finally {
      setSaving(false)
    }
  }

  // ── Next / prev room ──────────────────────────────────────────────────────────

  function handleNextRoom() {
    if (roomIndex < ROOMS.length - 1) {
      setRoomIndex(i => i + 1)
      setItemInput('')
    } else {
      setStep('done')
    }
  }

  function handlePrevRoom() {
    if (roomIndex > 0) {
      setRoomIndex(i => i - 1)
      setItemInput('')
    }
  }

  // ── Emoji / category helpers ──────────────────────────────────────────────────

  function getEmoji(name: string): string {
    const n = name.toLowerCase()
    const map: [string[], string][] = [
      [['milk', 'dairy'],                   '🥛'],
      [['egg'],                             '🥚'],
      [['butter', 'cream'],                 '🧈'],
      [['cheese'],                          '🧀'],
      [['chicken', 'poultry'],              '🍗'],
      [['beef', 'steak', 'burger'],         '🥩'],
      [['fish', 'salmon', 'tuna'],          '🐟'],
      [['bread', 'toast'],                  '🍞'],
      [['pasta', 'noodle', 'spaghetti'],    '🍝'],
      [['rice'],                            '🍚'],
      [['tomato'],                          '🍅'],
      [['apple'],                           '🍎'],
      [['banana'],                          '🍌'],
      [['orange'],                          '🍊'],
      [['coffee'],                          '☕'],
      [['tea'],                             '🍵'],
      [['juice'],                           '🧃'],
      [['water'],                           '💧'],
      [['yogurt'],                          '🥛'],
      [['cereal'],                          '🥣'],
      [['chocolate'],                       '🍫'],
      [['oil', 'olive'],                    '🫙'],
      [['garlic', 'onion'],                 '🧄'],
      [['potato'],                          '🥔'],
      [['carrot'],                          '🥕'],
      [['lettuce', 'salad', 'spinach'],     '🥬'],
    ]
    for (const [keywords, emoji] of map) {
      if (keywords.some(k => n.includes(k))) return emoji
    }
    return '📦'
  }

  function getCategory(roomId: string): string {
    switch (roomId) {
      case 'fridge':     return 'dairy'
      case 'freezer':    return 'frozen'
      case 'pantry':     return 'pantry'
      case 'cabinets':   return 'pantry'
      case 'countertop': return 'produce'
      default:           return 'other'
    }
  }

  function getFreshness(roomId: string): string {
    switch (roomId) {
      case 'fridge':     return 'dairy'
      case 'freezer':    return 'frozen'
      case 'countertop': return 'fresh_produce'
      default:           return 'pantry_staple'
    }
  }

  // ── Intro screen ──────────────────────────────────────────────────────────────

  if (step === 'intro') {
    return (
      <div className="fixed inset-0 z-50 bg-[#FAF8F4] flex flex-col items-center
                      justify-center px-6 max-w-[480px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full text-center"
        >
          <div className="w-20 h-20 rounded-3xl bg-[#6B9E78] flex items-center justify-center
                          text-4xl mx-auto mb-6 shadow-lg">
            🏠
          </div>

          <h1 className="text-2xl font-bold text-[#2C2A26] mb-3">
            Let's stock your pantry
          </h1>
          <p className="text-sm text-[#7A7468] leading-relaxed mb-8">
            Walk around your home and add what you already have.
            This gives Pantry a head start — your shopping list will be
            accurate from day one instead of taking weeks to learn.
          </p>

          {/* Benefits */}
          <div className="bg-white rounded-2xl border border-[#E8E2DC] p-4 mb-8 text-left">
            {[
              { emoji: '⚡', text: 'Predictions start immediately, not after weeks of use' },
              { emoji: '📋', text: 'Your first shopping list generates right away' },
              { emoji: '⏱️', text: 'Takes about 5–10 minutes for a typical home' },
            ].map((item, i) => (
              <div key={i} className={`flex items-start gap-3 ${i > 0 ? 'mt-3 pt-3 border-t border-[#F0EDE8]' : ''}`}>
                <span className="text-lg shrink-0">{item.emoji}</span>
                <p className="text-sm text-[#2C2A26]">{item.text}</p>
              </div>
            ))}
          </div>

          <button
            onClick={() => setStep('rooms')}
            className="w-full py-4 rounded-2xl text-base font-semibold text-white mb-3"
            style={{ background: '#6B9E78' }}
          >
            Let's do it 🚀
          </button>
          <button
            onClick={onSkip}
            className="w-full py-3 text-sm text-[#9A9491]"
          >
            Skip for now — I'll add items manually
          </button>
        </motion.div>
      </div>
    )
  }

  // ── Done screen ───────────────────────────────────────────────────────────────

  if (step === 'done') {
    return (
      <div className="fixed inset-0 z-50 bg-[#FAF8F4] flex flex-col items-center
                      justify-center px-6 max-w-[480px] mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', stiffness: 280, damping: 22 }}
          className="w-full text-center"
        >
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-[#2C2A26] mb-2">
            You're all set!
          </h1>
          <p className="text-sm text-[#7A7468] mb-6">
            Added <span className="font-bold text-[#6B9E78]">{totalAdded} items</span> across your home
          </p>

          {/* Room breakdown */}
          <div className="bg-white rounded-2xl border border-[#E8E2DC] p-4 mb-6 text-left">
            {ROOMS.map(room => {
              const count = roomCounts[room.id] ?? 0
              if (count === 0) return null
              return (
                <div key={room.id} className="flex items-center gap-3 py-2
                                              border-b border-[#F0EDE8] last:border-0">
                  <span className="text-xl">{room.emoji}</span>
                  <span className="text-sm text-[#2C2A26] flex-1">{room.label}</span>
                  <span className="text-sm font-semibold text-[#6B9E78]">{count} items</span>
                </div>
              )
            })}
          </div>

          <div className="bg-[#E8F2EA] rounded-2xl p-4 mb-6 text-left">
            <p className="text-sm font-semibold text-[#6B9E78] mb-1">What happens next</p>
            <p className="text-xs text-[#6B9E78]/80 leading-relaxed">
              Pantry will start tracking these items. After your first shopping trip,
              it'll begin predicting when you'll run out and auto-build your list.
            </p>
          </div>

          <button
            onClick={onComplete}
            className="w-full py-4 rounded-2xl text-base font-semibold text-white"
            style={{ background: '#6B9E78' }}
          >
            Go to my pantry →
          </button>
        </motion.div>
      </div>
    )
  }

  // ── Room screens ──────────────────────────────────────────────────────────────

  const roomItemsAdded = roomCounts[currentRoom.id] ?? 0

  return (
    <div className="fixed inset-0 z-50 bg-[#FAF8F4] flex flex-col max-w-[480px] mx-auto">

      {/* Progress bar */}
      <div className="w-full h-1 bg-[#E8E2DC]">
        <motion.div
          className="h-full"
          style={{ background: currentRoom.color }}
          animate={{ width: `${((roomIndex + 1) / ROOMS.length) * 100}%` }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
        />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 shrink-0">
        <button
          onClick={roomIndex === 0 ? () => setStep('intro') : handlePrevRoom}
          className="text-sm text-[#7A7468] font-medium"
        >
          ← Back
        </button>
        <div className="flex items-center gap-1.5">
          {ROOMS.map((r, i) => (
            <div
              key={r.id}
              className="h-1.5 rounded-full transition-all"
              style={{
                width:      i === roomIndex ? 20 : 6,
                background: i <= roomIndex ? currentRoom.color : '#E8E2DC',
              }}
            />
          ))}
        </div>
        <button
          onClick={onSkip}
          className="text-sm text-[#9A9491]"
        >
          Skip all
        </button>
      </div>

      {/* Room header */}
      <div
        className="mx-4 rounded-2xl p-4 mb-4 flex items-center gap-3 shrink-0"
        style={{ background: currentRoom.colorLight }}
      >
        <span className="text-3xl">{currentRoom.emoji}</span>
        <div>
          <p className="font-bold text-base" style={{ color: currentRoom.color }}>
            {currentRoom.label}
          </p>
          <p className="text-xs text-[#7A7468] mt-0.5">{currentRoom.hint}</p>
        </div>
        {roomItemsAdded > 0 && (
          <div
            className="ml-auto px-2.5 py-1 rounded-full text-xs font-bold text-white shrink-0"
            style={{ background: currentRoom.color }}
          >
            {roomItemsAdded} added
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-4 mb-4 shrink-0">
        <div className="flex gap-2">
          <input
            type="text"
            value={itemInput}
            onChange={e => setItemInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && itemInput.trim()) handleAddItem(itemInput)
            }}
            placeholder={`What's in your ${currentRoom.label.toLowerCase()}?`}
            autoFocus
            className="flex-1 px-4 py-3 rounded-xl border border-[#E8E2DC] bg-white
                       text-sm text-[#2C2A26] outline-none focus:border-[#6B9E78] transition-colors
                       placeholder-[#C5BFB8]"
          />
          <button
            onClick={() => handleAddItem(itemInput)}
            disabled={saving || !itemInput.trim()}
            className="px-4 py-3 rounded-xl text-white text-sm font-semibold
                       disabled:opacity-40 shrink-0"
            style={{ background: currentRoom.color }}
          >
            {saving ? '…' : 'Add'}
          </button>
        </div>
      </div>

      {/* Example chips */}
      <div className="px-4 mb-4 shrink-0">
        <p className="text-xs text-[#9A9491] mb-2">Tap to add quickly:</p>
        <div className="flex flex-wrap gap-2">
          {currentRoom.examples
            .filter(ex => !addedItems.some(a => a.name.toLowerCase() === ex.toLowerCase()))
            .map(example => (
              <button
                key={example}
                onClick={() => handleAddItem(example)}
                disabled={saving}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition-all
                           border disabled:opacity-40"
                style={{
                  background:  currentRoom.colorLight,
                  color:       currentRoom.color,
                  borderColor: currentRoom.color + '30',
                }}
              >
                + {example}
              </button>
            ))}
        </div>
      </div>

      {/* Recently added list */}
      <div className="flex-1 overflow-y-auto px-4">
        <AnimatePresence>
          {addedItems
            .filter(i => i.location === currentRoom.id)
            .reverse()
            .map((item, idx) => (
              <motion.div
                key={item.name + idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-3 py-2.5 border-b border-[#F0EDE8]"
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs shrink-0"
                  style={{ background: currentRoom.color }}
                >
                  ✓
                </div>
                <span className="text-sm text-[#2C2A26]">{item.name}</span>
              </motion.div>
            ))}
        </AnimatePresence>
      </div>

      {/* Bottom nav */}
      <div className="px-4 pb-8 pt-3 border-t border-[#F0EDE8] shrink-0">
        <button
          onClick={handleNextRoom}
          className="w-full py-4 rounded-2xl text-base font-semibold text-white"
          style={{ background: currentRoom.color }}
        >
          {roomIndex < ROOMS.length - 1
            ? `Next: ${ROOMS[roomIndex + 1].label} ${ROOMS[roomIndex + 1].emoji}`
            : 'Finish setup 🎉'}
        </button>
        {roomItemsAdded === 0 && (
          <button
            onClick={handleNextRoom}
            className="w-full mt-2 py-2 text-sm text-[#9A9491]"
          >
            Nothing here — skip this room
          </button>
        )}
      </div>
    </div>
  )
}
